import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import {
  inspectionService,
  INSPECTION_STATUS_LABELS,
  OPS_REVIEW_STATUS_LABELS,
  WARRANTY_STATUS_LABELS,
  type InspectionStatus,
  type OperationsReviewStatus,
} from '@/services/inspection.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { useDebounce } from '@/hooks/use-debounce';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;

const SELECT_CLASS =
  'flex h-10 rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

const STATUS_BADGE: Record<InspectionStatus, string> = {
  UNASSIGNED: 'bg-slate-500/10 text-slate-600',
  SCHEDULED: 'bg-sky-500/10 text-sky-700',
  ASSIGNED: 'bg-blue-500/10 text-blue-700',
  IN_PROGRESS: 'bg-amber-500/10 text-amber-700',
  TRANSFER_REQUESTED: 'bg-orange-500/10 text-orange-700',
  COMPLETED: 'bg-teal-500/10 text-teal-700',
  REVIEWED: 'bg-purple-500/10 text-purple-700',
  CLOSED: 'bg-green-500/10 text-green-700',
  CANCELLED: 'bg-red-500/10 text-red-700',
};

const OPS_BADGE: Record<OperationsReviewStatus, string> = {
  PENDING: 'bg-muted text-muted-foreground',
  RETURNED: 'bg-red-500/10 text-red-700',
  REVIEWED: 'bg-teal-500/10 text-teal-700',
  APPROVED_FOR_COMMERCIAL: 'bg-green-500/10 text-green-700',
};

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

export function InspectionListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InspectionStatus | ''>(
    (searchParams.get('status') as InspectionStatus | null) ?? '',
  );
  const [opsFilter, setOpsFilter] = useState<OperationsReviewStatus | ''>(
    (searchParams.get('operationsReviewStatus') as OperationsReviewStatus | null) ?? '',
  );
  // Dashboard-driven filters with no dedicated dropdown on this page yet —
  // applied once from the incoming link, not user-editable here.
  const commercialReviewStatus = searchParams.get('commercialReviewStatus') ?? undefined;
  const customerApprovalStatus = searchParams.get('customerApprovalStatus') ?? undefined;
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading } = useQuery({
    queryKey: [
      'inspections',
      page,
      debouncedSearch,
      statusFilter,
      opsFilter,
      commercialReviewStatus,
      customerApprovalStatus,
    ],
    queryFn: () =>
      inspectionService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        operationsReviewStatus: opsFilter || undefined,
        commercialReviewStatus,
        customerApprovalStatus,
      }),
  });

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader title="Inspections" description="View and manage inspection records" />

      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[220px]">
          <SearchBar
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search by inspection #, equipment, or customer…"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as InspectionStatus | '');
            setPage(1);
          }}
          className={SELECT_CLASS}
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          {(Object.keys(INSPECTION_STATUS_LABELS) as InspectionStatus[]).map((s) => (
            <option key={s} value={s}>
              {INSPECTION_STATUS_LABELS[s]}
            </option>
          ))}
        </select>

        <select
          value={opsFilter}
          onChange={(e) => {
            setOpsFilter(e.target.value as OperationsReviewStatus | '');
            setPage(1);
          }}
          className={SELECT_CLASS}
          aria-label="Filter by operations review"
        >
          <option value="">All Ops Status</option>
          {(Object.keys(OPS_REVIEW_STATUS_LABELS) as OperationsReviewStatus[]).map((s) => (
            <option key={s} value={s}>
              {OPS_REVIEW_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Inspection #</th>
                  <th className="px-4 py-3 font-medium text-foreground">Equipment</th>
                  <th className="px-4 py-3 font-medium text-foreground">Customer</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground">Warranty</th>
                  <th className="px-4 py-3 font-medium text-foreground">Ops Review</th>
                  <th className="px-4 py-3 font-medium text-foreground">Assigned</th>
                  <th className="px-4 py-3 font-medium text-foreground">Scheduled</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.data.map((inspection) => (
                  <tr
                    key={inspection.id}
                    className="cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => navigate(ROUTES.INSPECTION_DETAIL.replace(':id', inspection.id))}
                  >
                    <td className="px-4 py-3 font-mono font-medium text-primary">
                      {inspection.inspectionNumber}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-foreground">{inspection.equipmentName}</span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {inspection.equipmentAssetNumber}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {inspection.customerOrganizationName ?? inspection.customerName ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[inspection.status]}`}
                      >
                        {INSPECTION_STATUS_LABELS[inspection.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {WARRANTY_STATUS_LABELS[inspection.warrantyStatus]}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${OPS_BADGE[inspection.operationsReviewStatus]}`}
                      >
                        {OPS_REVIEW_STATUS_LABELS[inspection.operationsReviewStatus]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {inspection.assignedEngineerName ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {fmtDate(inspection.scheduledAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          icon={faClipboardCheck}
          title={
            debouncedSearch || statusFilter || opsFilter
              ? 'No inspections match your filters'
              : 'No inspections yet'
          }
          description={
            debouncedSearch || statusFilter || opsFilter
              ? 'Try adjusting your search or filters.'
              : 'Inspections are created from equipment inward records.'
          }
        />
      )}
    </div>
  );
}
